import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useHistoryMisc } from '@hooks/register/history/use-history-misc';
import { useLocalize } from '@hooks/use-localize';
import { useScrollToHash } from '@hooks/use-scroll-to-hash';
import { useStepForm } from '@hooks/use-step-form';
import { asStringBool } from '@/utils/parse';
import { historyMiscSchema } from '@views/register/history/history-misc.schema';
import { DashRegistrationStep, historyLinkMap } from '@/types/dashboard';
import type { IHistoryMiscFormValues, IHistoryMiscModel, IHistoryMiscSubmitResult } from '@/types/history/history-misc';
import AllergiesSection from '@views/register/history/misc-sections/allergies-section';
import SurgeriesSection from '@views/register/history/misc-sections/surgeries-section';
import MedicationsSection from '@views/register/history/misc-sections/medications-section';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';

const HistoryMisc = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useHistoryMisc(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <HistoryMiscContent model={model} submit={submit} isSubmitting={isSubmitting} submitError={submitError} />
        </PageLoader>
    );
};

interface IHistoryMiscContentProps {
    model: IHistoryMiscModel;
    submit: (values: IHistoryMiscFormValues) => Promise<IHistoryMiscSubmitResult>;
    isSubmitting: boolean;
    submitError: string | null;
}

const withBlankRow = <T,>(entries: T[], blank: T) => (entries.length > 0 ? entries : [blank]);

const HistoryMiscContent = ({ model, submit, isSubmitting, submitError }: IHistoryMiscContentProps) => {
    const t = useLocalize(model.languageData);

    useScrollToHash();

    const form = useStepForm<IHistoryMiscFormValues, IHistoryMiscSubmitResult>({
        resolver: zodResolver(historyMiscSchema),
        defaultValues: {
            hasAllergies: asStringBool(model.hasAllergies),
            allergies: withBlankRow(
                model.allergies.map((name) => ({ name })),
                { name: '' },
            ),
            hasHadSurgeries: asStringBool(model.hasHadSurgeries),
            surgeries: withBlankRow(model.surgeries, { name: '', when: '' }),
            takesMedication: asStringBool(model.takesMedication),
            medicines: withBlankRow(model.medicines, { name: '', dose: '', frequency: '' }),
        },
        submit,
        linkMap: historyLinkMap,
    });

    return (
        <StepFormLayout
            kind="history"
            model={model}
            step={DashRegistrationStep.HistoryMisc}
            title={t('DashLabel_AllergiesSurgeriesMeds', 'Allergies, Surgeries, and Meds')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div id="allergies">
                <AllergiesSection t={t} />
            </div>

            <div id="surgeries">
                <SurgeriesSection t={t} />
            </div>

            <div id="medications">
                <MedicationsSection t={t} />
            </div>
        </StepFormLayout>
    );
};

export default HistoryMisc;
